import * as React from 'react';
import { updateIfAvailable } from '../../Service/AppUpdateService';
import { Contract, Role, User } from 'tree-wallet-client';
import { userHasRole, UserRoles } from '../../Repository/UserRepository';
import { allowedRoles as formPermissions } from '../../Pages/FormPage/FormPage';
import { allowedRoles as listPermissions } from '../../Pages/MapPage/MapPage';
import { isAllowed, logout } from '../../Service/AuthService';
import CompassTwoTone from '@ant-design/icons/CompassTwoTone';
import PlusCircleTwoTone from '@ant-design/icons/PlusCircleTwoTone';
import SmileTwoTone from '@ant-design/icons/SmileTwoTone';
import CloudDownloadOutlined from '@ant-design/icons/CloudDownloadOutlined';
import { GlobalContext } from "../../Contexts/GlobalContext";
import { Filter } from '../../Pages/MapPage/MapPage';
import { askAndSaveOffline } from '../../Service/OfflineSaveService';
import { askAndDownloadKml } from '../../Service/KmlDownloadService';
import { isNativeApp } from '../../Dependency/NativeAppMemory';
import * as ContractRepository from '../../Repository/ContractRepository';

export interface SiteMenuProps {
  user?: User;
  contract?: Contract;
  setShowLoader: (show: boolean) => void;
  filter?: Filter;
}

interface SiteMenuState {
  opened: boolean;
}

export const linkTypes = {
  link: 'link',
  btn: 'btn',
  logout: 'logout',
};

export default class SiteMenu extends React.Component<SiteMenuProps, SiteMenuState> {
  static contextType = GlobalContext;
  declare context: React.ContextType<typeof GlobalContext>;

  constructor(props: SiteMenuProps) {
    super(props);
    this.toggle = this.toggle.bind(this);
    this.downloadkmlfile = this.downloadkmlfile.bind(this);
    this.saveOffline = this.saveOffline.bind(this);
    this.state = {
      opened: false
    };
  }

  // Every menu link starts a check for a new build, and follows its link straight away: the reload is
  // offered by the update prompt when the new version is ready (see `index.tsx`). `link.onClick` is
  // the menu toggle and is not called - doing so closed the desktop menu on every navigation.
  protected async followMenuLink(event: React.MouseEvent, navigate: any, to: string) {
    event.preventDefault();

    await updateIfAvailable();
    navigate(to);
  }

  async saveOffline(navigate?: any) {
    const { user } = this.props;
    const { filter, contract } = this.context;

    await this.goToTreeMap(navigate);

    // The same list `Download KML` takes: the filters applied on Tree Map, or everything the user may see
    await askAndSaveOffline({
      filter: filter || this.props.filter || { sortField: 'createdAt', sortOrder: 'desc' },
      admin: !!user && userHasRole(user.roles, UserRoles.Admin.name),
      contractId: contract?.id,
    });
  }

  // Both download links cover the trees list the map draws: 
  // the map is put on screen first, next the confirmation popup is displayed to the user
  protected async goToTreeMap(navigate?: any) {
    const goTo = navigate || this.context.navigate;
    const mapLocation = this.context.contract?.id ? `/${this.context.contract.id}/list` : null;

    if (goTo && mapLocation && window.location.pathname !== mapLocation) {
      goTo(mapLocation);
      // One frame: the route has to be the one on screen when the dialog appears
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }
  }

  toggle() {
    const { opened } = this.state;
    this.setState({
      opened: !opened
    });
  }

  async downloadkmlfile(navigate?: any) {
    const { user } = this.props;
    const { filter } = this.context;

    await this.goToTreeMap(navigate);

    // Same question as `Save Offline`, size included: it closes on the answer, then the overlay reports the download
    await askAndDownloadKml({
      filter: filter || this.props.filter || { sortField: 'createdAt', sortOrder: 'desc' },
      admin: !!user && userHasRole(user.roles, UserRoles.Admin.name),
    });
  }

  shouldDisplayLink(user: any, contract: Contract | undefined, link: any) {
    if (link.public) {
      return true;
    }
    if (!user && !link.public) {
      return false;
    }
    if (user && userHasRole(user.roles, UserRoles.Admin.name)) {
      return true;
    }
    if (link.type === linkTypes.logout) {
      return true;
    }

    const userRoles = user?.roles;
    return isAllowed([
      ...(contract?.roles || []),
      ...userRoles,
    ], link.permissions);
  }

  getLinks() {
    const { user, contract } = this.props;

    // The trees list copy is only ever written by this link: the app never downloads the list on its own. 
    // Validators are the ones who work on a site with no data connection in the fields
    const saveOfflineLink = { icon: <CloudDownloadOutlined />, type: linkTypes.btn, link: `#save-offline`, onClick: this.saveOffline, permissions: [ContractRepository.roles.Validator, UserRoles.Admin], text: 'Save Offline' };
    const offersSaveOffline = isNativeApp();
    let listedSaveOffline = false;

    const links = [];
    if (contract) {
      links.push({ icon: <PlusCircleTwoTone />, type: linkTypes.link, link: `/${contract.id}/form`, onClick: this.toggle, permissions: formPermissions, text: 'New Tree' });
      links.push({ icon: <CompassTwoTone />, type: linkTypes.link, link: `/${contract.id}/list`, onClick: this.toggle, permissions: listPermissions, text: 'Tree Map' });

      if (offersSaveOffline) {
        links.push(saveOfflineLink);
        listedSaveOffline = true;
      }
    }

    const adminLinks = [];
    if (user && userHasRole(user.roles, UserRoles.Admin.name)) {
      // No contract on the admin pages: the link sits with Download KML there, and never twice
      if (offersSaveOffline && !listedSaveOffline) {
        adminLinks.push(saveOfflineLink);
      }

      adminLinks.push({ icon: <SmileTwoTone />, type: linkTypes.btn, link: `#`, onClick: this.downloadkmlfile, permissions: [UserRoles.Admin.name], text: 'Download KML' });
      adminLinks.push({ icon: <SmileTwoTone />, type: linkTypes.link, link: `/admin`, onClick: this.toggle, permissions: [UserRoles.Admin.name], text: 'Manage Users' });
      adminLinks.push({ icon: <SmileTwoTone />, type: linkTypes.link, link: `/admin/contracts`, onClick: this.toggle, permissions: [UserRoles.Admin], text: 'Manage Contracts' });
    }

    const globalLinks = [
      { icon: <SmileTwoTone />, type: linkTypes.logout, onClick: logout, permissions: Object.values(UserRoles.all).map((role: Role) => role.name), text: 'Logout' }
    ];

    const publicLinks = [];
    if (!user) {
      publicLinks.push({ icon: <SmileTwoTone />, type: linkTypes.link, public: true, link: '/public', text: 'Our trees' });
      publicLinks.push({ icon: <SmileTwoTone />, type: linkTypes.link, public: true, link: '/login', text: 'Login' });
    }

    if (!user) {
      return publicLinks;
    }

    return [...publicLinks, ...links, ...adminLinks, ...globalLinks];
  }
}
